package com.mgacreative.hotel.feature.pricing.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.feature.pricing.domain.PricingEngine
import com.mgacreative.hotel.feature.pricing.domain.RoomTypePrice
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

import kotlinx.coroutines.launch
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest

class DynamicPricingViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) : ViewModel() {
    private val engine = PricingEngine()

    private val _roomTypes = MutableStateFlow<List<RoomTypePrice>>(emptyList())
    val roomTypes: StateFlow<List<RoomTypePrice>> = _roomTypes.asStateFlow()

    private val _isAutoApplyEnabled = MutableStateFlow(false)
    val isAutoApplyEnabled: StateFlow<Boolean> = _isAutoApplyEnabled.asStateFlow()

    private val _successMessage = MutableStateFlow<String?>(null)
    val successMessage: StateFlow<String?> = _successMessage.asStateFlow()

    // Mock Environmental Data
    private var currentOccupancy = 0.85 // 85% occupancy
    private var isHighSeason = true

    init {
        loadData()
    }

    @kotlinx.serialization.Serializable
    data class DbRoomType(
        val id: String,
        val name: String,
        val base_price: Double? = 100.0
    )

    private fun loadData() {
        viewModelScope.launch {
            try {
                val tenantId = tenantSession.currentTenantId
                if (!tenantId.isNullOrBlank()) {
                    val roomTypes = supabaseClient.client.postgrest["room_types"]
                        .select {
                            filter {
                                eq("tenant_id", tenantId)
                            }
                        }
                        .decodeList<DbRoomType>()
                    
                    _roomTypes.value = roomTypes.map { rt ->
                        val basePrice = rt.base_price ?: 100.0
                        val (suggested, rules) = engine.calculateSuggestion(basePrice, currentOccupancy, isHighSeason)
                        RoomTypePrice(rt.id, rt.name, basePrice, basePrice, suggested, rules)
                    }
                } else {
                    _roomTypes.value = emptyList()
                }
            } catch (e: Exception) {
                _roomTypes.value = emptyList()
            }
        }
    }

    fun toggleAutoApply(enabled: Boolean) {
        _isAutoApplyEnabled.value = enabled
        if (enabled) {
            // Apply all suggestions immediately
            _roomTypes.value = _roomTypes.value.map { it.copy(currentPrice = it.suggestedPrice) }
            _successMessage.value = "Otomatik fiyatlandırma aktif! Fiyatlar güncellendi."
        }
    }

    // Acceptance Criteria: Otomatik uygulama kapalıyken fiyatlar Hotel Owner onayıyla değişiyor
    fun approveSuggestion(roomTypeId: String) {
        if (_isAutoApplyEnabled.value) return // If auto is on, it's already applied

        _roomTypes.value = _roomTypes.value.map {
            if (it.id == roomTypeId) it.copy(currentPrice = it.suggestedPrice) else it
        }
        _successMessage.value = "Fiyat önerisi onaylandı ve aktif fiyata yansıdı."
    }
    
    fun clearMessage() {
        _successMessage.value = null
    }

    @kotlinx.serialization.Serializable
    data class DbPricePeriod(
        val tenant_id: String,
        val room_type: String,
        val name: String,
        val start_date: String,
        val end_date: String,
        val price: Double
    )

    fun importCsv(csvData: String) {
        viewModelScope.launch {
            try {
                val tenantId = tenantSession.currentTenantId ?: return@launch
                
                val lines = csvData.trim().split("\n")
                val periodsToInsert = mutableListOf<DbPricePeriod>()
                
                for (line in lines) {
                    if (line.isBlank()) continue
                    val parts = line.split(",")
                    if (parts.size >= 5) {
                        val roomType = parts[0].trim()
                        val name = parts[1].trim()
                        val startDate = parts[2].trim()
                        val endDate = parts[3].trim()
                        val price = parts[4].trim().toDoubleOrNull() ?: continue
                        
                        // Validasyon (örneğin ilk satır başlık olabilir)
                        if (price > 0) {
                            periodsToInsert.add(
                                DbPricePeriod(
                                    tenant_id = tenantId,
                                    room_type = roomType,
                                    name = name,
                                    start_date = startDate,
                                    end_date = endDate,
                                    price = price
                                )
                            )
                        }
                    }
                }
                
                if (periodsToInsert.isNotEmpty()) {
                    supabaseClient.client.postgrest["price_periods"]
                        .insert(periodsToInsert)
                    
                    _successMessage.value = "${periodsToInsert.size} adet sezon fiyatı başarıyla içe aktarıldı."
                } else {
                    _successMessage.value = "CSV formatı hatalı veya geçerli veri bulunamadı."
                }
            } catch (e: Exception) {
                e.printStackTrace()
                _successMessage.value = "İçe aktarma hatası: ${e.message}"
            }
        }
    }
}

